// After successful narration the late cursor fallback stays silent: reply
// speech reads only what the narration did not already say. A later failed
// presentation lifts this: its recovery instructions must be heard. A stopped
// presentation silences the rest of the turn.
// Keep this separate from short-lived exact-text dedup, since tool work can
// continue for minutes after the last narrated step.
type Outcome = "narrated" | "progress" | "failed" | "stopped";
const executionOutcomes = new Map<string, Outcome>();
const narrationCompletedAt = new Map<string, number>();
const MAX_RETAINED_EXECUTIONS = 256;

function remember(executionId: string, outcome: Outcome) {
  executionOutcomes.delete(executionId);
  executionOutcomes.set(executionId, outcome);
  if (executionOutcomes.size > MAX_RETAINED_EXECUTIONS) {
    const oldestExecutionId = executionOutcomes.keys().next().value!;
    executionOutcomes.delete(oldestExecutionId);
    narrationCompletedAt.delete(oldestExecutionId);
  }
}

export const presentationSpeechTurns = {
  outcome: (executionId: string) => executionOutcomes.get(executionId),
  has: (executionId: string) => {
    const outcome = executionOutcomes.get(executionId);
    return outcome === "narrated" || outcome === "stopped";
  },
  complete(executionId: string, answer = true) {
    // A narration may finish after the visual or foreground action fails.
    // It cannot silence the agent's subsequent explanation of that failure.
    const outcome = executionOutcomes.get(executionId);
    if (outcome !== "failed" && outcome !== "stopped") {
      remember(
        executionId,
        answer || outcome === "narrated" ? "narrated" : "progress",
      );
      narrationCompletedAt.set(executionId, Date.now());
    }
  },
  lastCompletedAt: (executionId: string) =>
    narrationCompletedAt.get(executionId),
  clear: (executionId: string) => {
    executionOutcomes.delete(executionId);
    narrationCompletedAt.delete(executionId);
  },
  reset: () => {
    executionOutcomes.clear();
    narrationCompletedAt.clear();
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("denker:presentation-stop", () => {
    for (const executionId of executionOutcomes.keys()) {
      executionOutcomes.set(executionId, "stopped");
    }
  });
  window.addEventListener("denker:presentation-delivery-failed", (event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!detail || typeof detail !== "object") return;
    const failure = detail as Record<string, unknown>;
    // This channel stops both failed and cancelled playback. Only actual
    // failures should enable a spoken recovery explanation.
    if (
      typeof failure.executionId !== "string" ||
      !failure.executionId ||
      typeof failure.presentationId !== "string" ||
      !failure.presentationId
    )
      return;
    if (failure.outcome === "cancelled") {
      remember(failure.executionId, "stopped");
      return;
    }
    if (
      failure.outcome !== "failed" ||
      typeof failure.playbackAttempt !== "number" ||
      !Number.isSafeInteger(failure.playbackAttempt) ||
      failure.playbackAttempt < 1
    )
      return;
    if (executionOutcomes.get(failure.executionId) !== "stopped") {
      remember(failure.executionId, "failed");
    }
  });
}
